import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { XCircle, Search, Loader2, CheckCircle, Clock, Mail, Phone, User, X } from 'lucide-react';
import AbstractGuidelines from '@/components/AbstractGuidelines';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AbstractSubmissionPage = () => {
  const [searchType, setSearchType] = useState('email');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [abstracts, setAbstracts] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) { toast.error('Please enter your email or phone number'); return; }
    setSearching(true); setAbstracts(null); setNotFound(false);
    try {
      const res = await axios.get(`${API}/abstract-status?type=${searchType}&q=${encodeURIComponent(searchQuery.trim())}`);
      if (res.data && res.data.length > 0) setAbstracts(res.data);
      else setNotFound(true);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error('Search failed. Please try again.');
    } finally { setSearching(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusMeta = (status) => {
    if (status === 'accepted') return { label: 'Selected', cls: 'bg-green-500/20 text-green-400' };
    if (status === 'rejected') return { label: 'Not Selected', cls: 'bg-red-500/20 text-red-400' };
    return { label: 'Under Review', cls: 'bg-yellow-500/20 text-yellow-400' };
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="abstract-submission-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4" data-testid="abstract-title">
            Abstract <span className="gradient-text">Submission</span>
          </h1>
          <p className="text-lg text-slate-300">Share your research with the ophthalmology community</p>
        </div>

        {/* Submission Closed */}
        <div className="glass-card p-8 md:p-10 mb-8" data-testid="abstract-closed-banner">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-red-400" data-testid="abstract-closed-title">Abstract Submission Closed</h2>
          </div>
          <p className="text-slate-300">The deadline for abstract submission has passed. No further submissions are being accepted. Delegates who have already submitted can check their selection status below.</p>
        </div>

        {/* Check My Abstract Status */}
        <div className="glass-card p-8 md:p-10 mb-10" data-testid="check-abstract-section">
          <div className="flex items-center gap-3 mb-5">
            <Search className="h-6 w-6 text-[#FF6F4A]" />
            <h2 className="text-xl font-bold text-white">Check My Abstract Status</h2>
          </div>
          <p className="text-slate-400 text-sm mb-5">Search by your registered email or phone number to see whether your abstract has been selected and whether it is a Paper or Poster.</p>

          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => { setSearchType('email'); setSearchQuery(''); setAbstracts(null); setNotFound(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${searchType === 'email' ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300'}`}
                data-testid="abstract-search-email-tab"><Mail className="h-4 w-4 inline mr-1" /> Email</button>
              <button type="button" onClick={() => { setSearchType('phone'); setSearchQuery(''); setAbstracts(null); setNotFound(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${searchType === 'phone' ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300'}`}
                data-testid="abstract-search-phone-tab"><Phone className="h-4 w-4 inline mr-1" /> Phone</button>
            </div>
            <div className="flex gap-3">
              <Input type={searchType === 'email' ? 'email' : 'tel'} placeholder={searchType === 'email' ? 'Enter your email address' : 'Enter your phone number'}
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="glass-card border-white/20 text-white flex-1" data-testid="abstract-search-input" />
              <Button type="submit" disabled={searching} className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-6" data-testid="abstract-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </form>

          {notFound && (
            <div className="mt-6 p-4 glass-card border-red-500/20 bg-red-500/5 text-center" data-testid="abstract-not-found">
              <p className="text-red-400">No abstract submissions found for this {searchType}.</p>
            </div>
          )}

          {abstracts && abstracts.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="abstract-results">
              <p className="text-sm text-slate-400">Found {abstracts.length} abstract(s):</p>
              {abstracts.map((ab, idx) => {
                const meta = statusMeta(ab.status);
                return (
                  <div key={ab.id || idx} className="glass-card p-5 border border-white/10" data-testid={`abstract-result-${idx}`}>
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-[#FF6F4A]" />
                        <span className="text-white font-semibold">{ab.author_name}</span>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${meta.cls}`} data-testid={`abstract-status-${idx}`}>
                        {meta.label}
                      </span>
                    </div>
                    <h3 className="text-white font-medium mb-2">{ab.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 mb-2">
                      <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-300">{ab.category}</span>
                      {ab.presentation_type && ab.presentation_type !== 'none' && (
                        <span className={`px-2 py-1 rounded-full font-semibold ${ab.presentation_type === 'paper' ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'}`} data-testid={`abstract-type-${idx}`}>
                          {ab.presentation_type === 'paper' ? 'Free Paper' : 'E-Poster'}
                        </span>
                      )}
                      <span>Submitted: {formatDate(ab.created_at)}</span>
                    </div>
                    {(ab.status === 'submitted' || ab.status === 'under_review') && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-yellow-400">
                        <Clock className="h-3 w-3" />
                        <span>Your abstract is under review. Faculty will contact you directly.</span>
                      </div>
                    )}
                    {ab.status === 'accepted' && (ab.presentation_type === 'paper' || ab.presentation_type === 'poster') && (
                      <div className="mt-3 p-3 glass-card border-[#FF6F4A]/20 bg-[#FF6F4A]/5">
                        <p className="text-xs text-[#FF6F4A] font-semibold mb-1">
                          Selected as {ab.presentation_type === 'paper' ? 'Free Paper' : 'E-Poster'}
                        </p>
                        <p className="text-xs text-slate-300">
                          Please submit your final {ab.presentation_type === 'paper' ? 'paper' : 'poster'} by <span className="text-white font-semibold">7th October 2026</span>. See submission details in the guidelines below.
                        </p>
                      </div>
                    )}
                    {ab.status === 'accepted' && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-green-400">
                        <CheckCircle className="h-3 w-3" />
                        <span>Congratulations! Please ensure you are an AGOS member for presentation.</span>
                      </div>
                    )}
                    {ab.status === 'rejected' && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-red-400">
                        <X className="h-3 w-3" />
                        <span>Thank you for your submission. Unfortunately, your abstract was not selected this time.</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Full Guidelines */}
        <AbstractGuidelines showSubmission={true} />

        <div className="mt-8 text-center text-sm text-slate-400">
          <p>For queries: <a href="mailto:Shobitanair_2@yahoo.co.in" className="text-[#FF6F4A] hover:underline">Shobitanair_2@yahoo.co.in</a> | Dr. Shobita Shah — 9585817219</p>
        </div>
      </div>
    </div>
  );
};

export default AbstractSubmissionPage;
